<template>
  <div class="orders-page">
    <main class="orders-content">
      <div v-if="loading && !products.length" class="page-state">正在加载产品...</div>
      <div v-else-if="errorMessage" class="page-state page-state--error">
        <Icon type="ios-alert-outline" />
        <b>检测项目加载失败</b>
        <span>{{ errorMessage }}</span>
        <button @click="retryLoad">重新加载</button>
      </div>
      <div v-else-if="!products.length" class="page-state page-state--empty">
        <Icon type="ios-paper-outline" />
        <b>暂无绑定产品</b>
        <span>信息采集完成并绑定产品后会在这里显示</span>
      </div>
      <ProjectCard v-for="product in products" :key="product.id" :product="product" @open="openProduct" />
      <div v-if="products.length < total" ref="loadMoreTrigger" class="load-more-state">
        <template v-if="loadMoreError">
          <span>加载失败</span>
          <button @click="retryLoadMore">点击重试</button>
        </template>
        <template v-else>
          <i v-if="loading" class="load-more-state__spinner"></i>
          <span>{{ loading ? '正在加载...' : '继续上滑加载更多' }}</span>
        </template>
      </div>
      <div v-else-if="products.length" class="load-more-state load-more-state--end">没有更多了</div>
    </main>
  </div>
</template>

<script>
import Qs from 'qs'
import { ensureH5Session, retainedQuery } from '../utils/session'
import ProjectCard from '../components/ProjectCard'

export default {
  name: 'ProjectList',
  components: { ProjectCard },
  data () {
    return {
      pageNum: 1,
      pageSize: 10,
      total: 0,
      products: [],
      loading: false,
      errorMessage: '',
      loadMoreError: '',
      loadObserver: null,
      useScrollFallback: false
    }
  },
  async created () {
    try {
      await ensureH5Session(this)
      await this.fetchOrders(true)
    } catch (error) {
      this.errorMessage = error.message || '检测项目加载失败'
    }
  },
  mounted () {
    this.useScrollFallback = typeof window.IntersectionObserver !== 'function'
    if (this.useScrollFallback) {
      window.addEventListener('scroll', this.handleScroll, { passive: true })
    }
  },
  beforeDestroy () {
    if (this.loadObserver) this.loadObserver.disconnect()
    if (this.useScrollFallback) window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    async fetchOrders (reset, targetPage) {
      if (this.loading) return
      if (reset) {
        this.pageNum = 1
        this.total = 0
        this.products = []
      }
      const requestPage = reset ? 1 : (targetPage || this.pageNum)
      this.loading = true
      this.errorMessage = ''
      this.loadMoreError = ''
      try {
        const res = await this.$http.post('/eps/getSnList', Qs.stringify({ pageNum: requestPage, pageSize: this.pageSize }))
        if (res.result !== '1') throw new Error(res.desc || '产品加载失败')
        const page = res.data || {}
        this.products = reset ? (page.list || []) : this.products.concat(page.list || [])
        this.total = Number(page.total || 0)
        this.pageNum = requestPage
      } catch (error) {
        if (this.products.length) {
          this.loadMoreError = error.message || '加载更多失败'
        } else {
          this.errorMessage = error.message || '检测项目加载失败'
        }
      } finally {
        this.loading = false
        this.$nextTick(this.observeLoadTrigger)
      }
    },
    loadMore () {
      if (this.loading || this.loadMoreError || this.products.length >= this.total) return
      this.fetchOrders(false, this.pageNum + 1)
    },
    retryLoadMore () {
      this.loadMoreError = ''
      this.fetchOrders(false, this.pageNum + 1)
    },
    observeLoadTrigger () {
      if (this.useScrollFallback || this.loadMoreError || !this.$refs.loadMoreTrigger) return
      if (this.loadObserver) this.loadObserver.disconnect()
      this.loadObserver = new window.IntersectionObserver(entries => {
        if (entries[0] && entries[0].isIntersecting) this.loadMore()
      }, {
        root: null,
        rootMargin: '160px 0px',
        threshold: 0.01
      })
      this.loadObserver.observe(this.$refs.loadMoreTrigger)
    },
    handleScroll () {
      if (this.loading || this.loadMoreError || this.products.length >= this.total) return
      const documentElement = document.documentElement
      const body = document.body
      const scrollTop = window.pageYOffset || documentElement.scrollTop || body.scrollTop || 0
      const viewportHeight = window.innerHeight || documentElement.clientHeight || 0
      const documentHeight = Math.max(documentElement.scrollHeight, body.scrollHeight)
      if (documentHeight - scrollTop - viewportHeight <= 160) this.loadMore()
    },
    async retryLoad () {
      try {
        await ensureH5Session(this)
        await this.fetchOrders(true)
      } catch (error) {
        this.errorMessage = error.message || '检测项目加载失败'
      }
    },
    openProduct (product) {
      this.$router.push({ path: '/projects/' + encodeURIComponent(product.id), query: retainedQuery({ patientId: product.patientId || '', serialNum: product.serialNum || '' }) })
    }
  }
}
</script>

<style scoped>
.orders-page { min-height: 100vh; padding-bottom: calc(82px + constant(safe-area-inset-bottom)); padding-bottom: calc(82px + env(safe-area-inset-bottom)); background: var(--wpc-bg); }
.orders-content { max-width: 620px; margin: 0 auto; padding: 12px; display: grid; gap: 12px; }
.page-state { margin-top: 50px; text-align: center; color: #8e98a8; }
.page-state--error { display: flex; flex-direction: column; align-items: center; gap: 7px; color: #d45d5d; }
.page-state--error > i { font-size: 40px; }
.page-state--error b { color: var(--wpc-text); }
.page-state--error span { font-size: 12px; }
.page-state--error button { min-height: 36px; margin-top: 8px; padding: 7px 22px; border: 1px solid #b9d4f1; border-radius: 8px; color: var(--wpc-primary); background: #fff; }
.page-state--empty { display: flex; flex-direction: column; align-items: center; gap: 7px; }
.page-state--empty > i { font-size: 48px; color: #9bb7dc; }
.page-state--empty b { color: #5f6b7d; }
.page-state--empty span { font-size: 12px; }
.load-more-state { min-height: 46px; display: flex; align-items: center; justify-content: center; gap: 8px; color: #7b8797; font-size: 12px; }
.load-more-state--end { color: #a0a8b4; }
.load-more-state button { padding: 6px 8px; border: 0; color: var(--wpc-primary); background: transparent; font-size: 12px; }
.load-more-state__spinner { width: 14px; height: 14px; border: 2px solid #d7e3f1; border-top-color: var(--wpc-primary); border-radius: 50%; animation: load-more-rotate .8s linear infinite; }
@keyframes load-more-rotate { to { transform: rotate(360deg); } }
@media (max-width: 340px) { .orders-content { padding-right: 10px; padding-left: 10px; } }
</style>



// WEBPACK FOOTER //
// src/views/OrderList.vue